import React, { useEffect, useState, useMemo } from 'react';
import { Container, Table, Spinner, Alert, Card, Form, Row, Col, Badge, ProgressBar, ButtonGroup, Button } from 'react-bootstrap';
import axios from 'axios';
import { Pie, Bar } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  BarElement,
  Title 
} from 'chart.js';
import { FaUsers, FaCheckCircle, FaTimesCircle, FaClock, FaChartBar, FaSearch, FaGraduationCap, FaFilter } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import '../styles/ResumenCapacitaciones.css';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

// Normalizar API_URL (remover barra final si existe)
const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

// Helper para obtener headers de auth
const getAuthHeaders = () => {
  const storedUser = localStorage.getItem('user');
  const user = storedUser ? JSON.parse(storedUser) : null;
  const token = localStorage.getItem('token');
  
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-user-id': String(user?.Legajo || user?.CdEmpleado || '1'),
    'x-user-role': String(user?.IdRol || user?.tipoUsuario || '1')
  };
};

// Opciones de filtro por estado
const FILTROS_ESTADO = {
  TODOS: 'todos',
  APROBADOS: 'aprobado',
  NO_APROBADOS: 'no_aprobado',
  PENDIENTES: 'pendiente'
};

const ResumenCapacitaciones = () => {
  const [categorias, setCategorias] = useState([]);
  const [resumenes, setResumenes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busquedaGlobal, setBusquedaGlobal] = useState('');
  const [filtrosTabla, setFiltrosTabla] = useState({});
  const [categoriaExpandida, setCategoriaExpandida] = useState(null);
  // Filtro de estado por módulo (cada módulo tiene su propio filtro)
  const [filtrosEstado, setFiltrosEstado] = useState({});
  // Estado para mostrar/ocultar capacitaciones inactivas
  const [mostrarInactivas, setMostrarInactivas] = useState(false);

  // Helper para obtener el filtro de estado de un módulo específico
  const getFiltroEstadoModulo = (moduloId) => filtrosEstado[moduloId] || FILTROS_ESTADO.TODOS;
  const setFiltroEstadoModulo = (moduloId, estado) => {
    setFiltrosEstado(prev => ({ ...prev, [moduloId]: estado }));
  };

  useEffect(() => {
    const cargarPanelGlobal = async () => {
      try {
        setLoading(true);
        console.log('🔄 Cargando capacitaciones... Mostrar inactivas:', mostrarInactivas);
        console.log('🌐 API_URL:', API_URL);
        console.log('🔗 URL completa:', `${API_URL}/api/capacitaciones/categorias-con-modulos?incluirInactivas=${mostrarInactivas}`);

        const catRes = await axios.get(`${API_URL}/api/capacitaciones/categorias-con-modulos`, {
          headers: getAuthHeaders(),
          params: { incluirInactivas: mostrarInactivas }
        });
        console.log('📦 Categorías recibidas:', catRes.data.data?.length);
        console.log('📋 Datos completos:', catRes.data.data);
        const categoriasData = catRes.data.data;
        setCategorias(categoriasData);

        // Cargar todos los resúmenes en paralelo para mejor performance
        const modulosIds = categoriasData.flatMap(cat => cat.modulos.map(m => m.id));
        const resumenesPromises = modulosIds.map(id => 
          axios.get(`${API_URL}/api/capacitaciones/resumen/${id}`, {
            headers: getAuthHeaders()
          })
            .then(res => ({ id, data: res.data }))
            .catch(() => ({ id, data: null }))
        );
        
        const resumenesData = await Promise.all(resumenesPromises);
        const resumenesPorModulo = {};
        resumenesData.forEach(({ id, data }) => {
          if (data) {
            resumenesPorModulo[id] = data;
            console.log(`📊 Módulo ${id}:`, {
              total: data.total,
              aprobaron: data.aprobaron,
              detalle_length: data.detalle?.length || 0,
              detalle_sample: data.detalle?.slice(0, 3) || []
            });
          }
        });

        setResumenes(resumenesPorModulo);
        
        console.log('✅ Resúmenes cargados:', Object.keys(resumenesPorModulo).length);
        console.log('   IDs de módulos con resumen:', Object.keys(resumenesPorModulo).join(', '));
        
        // Expandir primera categoría por defecto
        if (categoriasData.length > 0) {
          setCategoriaExpandida(categoriasData[0].id);
          console.log('🔽 Categoría expandida:', categoriasData[0].nombre, '(ID:', categoriasData[0].id, ')');
        }
      } catch (err) {
        setError('No se pudo cargar el panel global');
      } finally {
        setLoading(false);
      }
    };

    cargarPanelGlobal();
  }, [mostrarInactivas]);

  // Calcular estadísticas globales
  const estadisticasGlobales = useMemo(() => {
    let totalEmpleados = 0;
    let totalAprobados = 0;
    let totalNoAprobados = 0;
    let totalPendientes = 0;

    Object.values(resumenes).forEach(resumen => {
      totalEmpleados += resumen.total || 0;
      totalAprobados += resumen.aprobaron || 0;
      totalNoAprobados += resumen.noAprobaron || 0;
      totalPendientes += resumen.pendientes || 0;
    });

    const tasaAprobacion = totalEmpleados > 0 
      ? Math.round((totalAprobados / totalEmpleados) * 100) 
      : 0;

    return { totalEmpleados, totalAprobados, totalNoAprobados, totalPendientes, tasaAprobacion };
  }, [resumenes]);

  // Datos para gráfico de barras comparativo
  const datosBarras = useMemo(() => {
    const labels = [];
    const aprobados = [];
    const noAprobados = [];
    const pendientes = [];

    categorias.forEach(cat => {
      cat.modulos.forEach(modulo => {
        const resumen = resumenes[modulo.id];
        if (resumen) {
          labels.push(modulo.titulo.length > 20 ? modulo.titulo.substring(0, 20) + '...' : modulo.titulo);
          aprobados.push(resumen.aprobaron);
          noAprobados.push(resumen.noAprobaron);
          pendientes.push(resumen.pendientes);
        }
      });
    });

    return {
      labels,
      datasets: [
        {
          label: 'Aprobados',
          data: aprobados,
          backgroundColor: 'rgba(25, 135, 84, 0.8)',
          borderRadius: 4,
        },
        {
          label: 'No aprobados',
          data: noAprobados,
          backgroundColor: 'rgba(220, 53, 69, 0.8)',
          borderRadius: 4,
        },
        {
          label: 'Pendientes',
          data: pendientes,
          backgroundColor: 'rgba(255, 193, 7, 0.8)',
          borderRadius: 4,
        },
      ],
    };
  }, [categorias, resumenes]);

  const opcionesBarras = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: false },
    },
    scales: {
      x: { stacked: false },
      y: { beginAtZero: true },
    },
  };

  // Filtro global
  const filtrarCategorias = categorias.filter(cat => {
    const termino = busquedaGlobal.toLowerCase();
    return (
      cat.nombre.toLowerCase().includes(termino) ||
      cat.modulos.some(m => m.titulo.toLowerCase().includes(termino))
    );
  });
  
  console.log('📊 Estado actual:');
  console.log('   Categorías totales:', categorias.length);
  console.log('   Categorías filtradas:', filtrarCategorias.length);
  console.log('   Búsqueda global:', busquedaGlobal || '(vacía)');
  console.log('   Mostrar inactivas:', mostrarInactivas);

  const getEstadoBadge = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'aprobado':
        return <Badge bg="success"><FaCheckCircle className="me-1" />Aprobado</Badge>;
      case 'no_aprobado':
        return <Badge bg="danger"><FaTimesCircle className="me-1" />No aprobado</Badge>;
      case 'pendiente':
        return <Badge bg="warning" text="dark"><FaClock className="me-1" />Pendiente</Badge>;
      default:
        return <Badge bg="secondary">{estado || 'Sin estado'}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Cargando dashboard...</p>
      </div>
    );
  }

  if (error) return <Alert variant="danger" className="m-4">{error}</Alert>;

  return (
    <div className="resumen-capacitaciones">
      <Container fluid className="py-4 px-4">
        {/* Header */}
        <div className="dashboard-header mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2><FaChartBar className="me-2" />Dashboard de Capacitaciones</h2>
              <p className="text-muted mb-0">Seguimiento del progreso y resultados de capacitaciones</p>
            </div>
            <div>
              <Form.Check 
                type="switch"
                id="toggle-inactivas"
                label="Mostrar inactivas"
                checked={mostrarInactivas}
                onChange={(e) => setMostrarInactivas(e.target.checked)}
                className="fs-6"
              />
            </div>
          </div>
        </div>

        {/* KPIs Cards - Solo informativos */}
        <Row className="g-3 mb-4">
          <Col xs={6} lg={3}>
            <Card className="kpi-card kpi-total">
              <Card.Body>
                <div className="kpi-icon"><FaUsers /></div>
                <div className="kpi-content">
                  <span className="kpi-value">{estadisticasGlobales.totalEmpleados}</span>
                  <span className="kpi-label">Total Registros</span>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} lg={3}>
            <Card className="kpi-card kpi-aprobados">
              <Card.Body>
                <div className="kpi-icon"><FaCheckCircle /></div>
                <div className="kpi-content">
                  <span className="kpi-value">{estadisticasGlobales.totalAprobados}</span>
                  <span className="kpi-label">Aprobados</span>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} lg={3}>
            <Card className="kpi-card kpi-noaprobados">
              <Card.Body>
                <div className="kpi-icon"><FaTimesCircle /></div>
                <div className="kpi-content">
                  <span className="kpi-value">{estadisticasGlobales.totalNoAprobados}</span>
                  <span className="kpi-label">No Aprobados</span>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} lg={3}>
            <Card className="kpi-card kpi-pendientes">
              <Card.Body>
                <div className="kpi-icon"><FaClock /></div>
                <div className="kpi-content">
                  <span className="kpi-value">{estadisticasGlobales.totalPendientes}</span>
                  <span className="kpi-label">Pendientes</span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tasa de aprobación global */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold">Tasa de Aprobación Global</span>
              <span className="fs-4 fw-bold text-success">{estadisticasGlobales.tasaAprobacion}%</span>
            </div>
            <ProgressBar>
              <ProgressBar 
                variant="success" 
                now={estadisticasGlobales.tasaAprobacion} 
                key={1} 
              />
              <ProgressBar 
                variant="danger" 
                now={estadisticasGlobales.totalEmpleados > 0 
                  ? (estadisticasGlobales.totalNoAprobados / estadisticasGlobales.totalEmpleados) * 100 
                  : 0} 
                key={2} 
              />
              <ProgressBar 
                variant="warning" 
                now={estadisticasGlobales.totalEmpleados > 0 
                  ? (estadisticasGlobales.totalPendientes / estadisticasGlobales.totalEmpleados) * 100 
                  : 0} 
                key={3} 
              />
            </ProgressBar>
            <div className="d-flex justify-content-between mt-2 small text-muted">
              <span>🟢 Aprobados</span>
              <span>🔴 No aprobados</span>
              <span>🟡 Pendientes</span>
            </div>
          </Card.Body>
        </Card>

        {/* Gráfico de barras comparativo */}
        {datosBarras.labels.length > 0 && (
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Header className="bg-white border-0">
              <h5 className="mb-0"><FaChartBar className="me-2" />Comparativa por Módulo</h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Bar data={datosBarras} options={opcionesBarras} />
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Buscador global */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body>
            <Row className="g-3 align-items-center">
              <Col md={8}>
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <Form.Control
                    type="text"
                    placeholder="Buscar capacitación o módulo..."
                    value={busquedaGlobal}
                    onChange={(e) => setBusquedaGlobal(e.target.value)}
                    className="search-input"
                  />
                </div>
              </Col>
              <Col md={4}>
                <div className="d-flex align-items-center gap-2">
                  <FaFilter className="text-muted" />
                  <span className="text-muted small">Usa los filtros dentro de cada módulo</span>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        {filtrarCategorias.length === 0 && (
          <Alert variant="warning">No se encontraron capacitaciones que coincidan con la búsqueda.</Alert>
        )}

        {/* Detalle por capacitación */}
        {filtrarCategorias.map(cat => (
          <Card key={cat.id} className="mb-4 border-0 shadow-sm categoria-card">
            <Card.Header 
              className="categoria-header"
              onClick={() => setCategoriaExpandida(categoriaExpandida === cat.id ? null : cat.id)}
              style={{ cursor: 'pointer' }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1">
                    <FaGraduationCap className="me-2 text-primary" />
                    {cat.nombre}
                    {!cat.activo && (
                      <Badge bg="secondary" className="ms-2" pill>Inactiva</Badge>
                    )}
                  </h5>
                  <small className="text-muted">
                    {cat.modulos.length} módulo(s) • Destinado a: {cat.rol_destinado === '0' ? 'Todos' : cat.rol_destinado}
                  </small>
                </div>
                <Badge bg="primary" pill>{cat.modulos.length}</Badge>
              </div>
            </Card.Header>

            {categoriaExpandida === cat.id && (
              <Card.Body>
                {cat.modulos.length === 0 ? (
                  <p className="text-muted text-center py-3">Sin módulos cargados</p>
                ) : (
                  cat.modulos.map(modulo => {
                    const resumen = resumenes[modulo.id];
                    if (!resumen) return null;

                    const pieData = {
                      labels: ['Aprobados', 'No aprobados', 'Pendientes'],
                      datasets: [{
                        data: [resumen.aprobaron, resumen.noAprobaron, resumen.pendientes],
                        backgroundColor: ['#198754', '#dc3545', '#ffc107'],
                        borderWidth: 0,
                      }],
                    };

                    const pieOptions = {
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { position: 'bottom', labels: { boxWidth: 12, padding: 8 } },
                      },
                    };

                    const filtro = filtrosTabla[modulo.id]?.toLowerCase() || '';
                    const filtroEstadoModulo = getFiltroEstadoModulo(modulo.id);
                    
                    // Aplicar filtro de búsqueda por texto Y filtro por estado
                    const detalleFiltrado = (resumen.detalle || []).filter(d => {
                      // Filtro de texto (legajo o nombre)
                      const pasaFiltroTexto = !filtro || 
                        d.legajo?.toString().includes(filtro) ||
                        d.nombre?.toLowerCase().includes(filtro);
                      
                      // Si es "todos", no filtrar por estado
                      if (filtroEstadoModulo === FILTROS_ESTADO.TODOS) {
                        return pasaFiltroTexto;
                      }
                      
                      // Filtro por estado - comparar en minúsculas
                      const estadoEmpleado = (d.estado || '').toLowerCase().trim();
                      let estadoEsperado = '';
                      
                      if (filtroEstadoModulo === FILTROS_ESTADO.APROBADOS) {
                        estadoEsperado = 'aprobado';
                      } else if (filtroEstadoModulo === FILTROS_ESTADO.NO_APROBADOS) {
                        estadoEsperado = 'no_aprobado';
                      } else if (filtroEstadoModulo === FILTROS_ESTADO.PENDIENTES) {
                        estadoEsperado = 'pendiente';
                      }
                      
                      return pasaFiltroTexto && estadoEmpleado === estadoEsperado;
                    });

                    // Tasa de aprobación: de los que intentaron (aprobados + no aprobados), cuántos aprobaron
                    const intentaron = resumen.aprobaron + resumen.noAprobaron;
                    const porcentajeAprobacion = intentaron > 0 
                      ? Math.round((resumen.aprobaron / intentaron) * 100) 
                      : 0;

                    return (
                      <div key={modulo.id} className="modulo-section">
                        <div className="modulo-header">
                          <h6 className="modulo-titulo">
                            {modulo.titulo}
                            {!modulo.activo && (
                              <Badge bg="secondary" className="ms-2" style={{fontSize: '0.7rem'}}>Inactivo</Badge>
                            )}
                          </h6>
                          <Badge bg={porcentajeAprobacion >= 70 ? 'success' : porcentajeAprobacion >= 40 ? 'warning' : 'danger'}>
                            {porcentajeAprobacion}% aprobación
                          </Badge>
                        </div>

                        <Row className="g-3">
                          {/* Estadísticas del módulo */}
                          <Col lg={4}>
                            <div className="modulo-stats">
                              <div className="stat-item">
                                <span className="stat-label">Total</span>
                                <span className="stat-value">{resumen.total}</span>
                              </div>
                              <div className="stat-item stat-aprobados">
                                <span className="stat-label">Aprobados</span>
                                <span className="stat-value">{resumen.aprobaron}</span>
                              </div>
                              <div className="stat-item stat-noaprobados">
                                <span className="stat-label">No aprobados</span>
                                <span className="stat-value">{resumen.noAprobaron}</span>
                              </div>
                              <div className="stat-item stat-pendientes">
                                <span className="stat-label">Pendientes</span>
                                <span className="stat-value">{resumen.pendientes}</span>
                              </div>
                            </div>

                            {/* Gráfico de torta */}
                            <div className="pie-container">
                              <Pie data={pieData} options={pieOptions} />
                            </div>
                          </Col>

                          {/* Tabla de detalle */}
                          <Col lg={8}>
                            <div className="tabla-header">
                              <div className="d-flex align-items-center flex-wrap gap-2">
                                <span className="fw-bold">Detalle por empleado</span>
                                <Badge bg="light" text="dark">
                                  {detalleFiltrado.length} de {resumen.detalle?.length || 0}
                                </Badge>
                              </div>
                              <Form.Control
                                type="text"
                                size="sm"
                                placeholder="🔍 Buscar..."
                                value={filtrosTabla[modulo.id] || ''}
                                onChange={(e) =>
                                  setFiltrosTabla({ ...filtrosTabla, [modulo.id]: e.target.value })
                                }
                                className="tabla-search"
                              />
                            </div>
                            
                            {/* Filtros por estado dentro de cada módulo */}
                            <div className="filtros-estado-modulo mb-2">
                              <ButtonGroup size="sm" className="w-100">
                                <Button 
                                  variant={filtroEstadoModulo === FILTROS_ESTADO.TODOS ? 'secondary' : 'outline-secondary'}
                                  onClick={() => setFiltroEstadoModulo(modulo.id, FILTROS_ESTADO.TODOS)}
                                  className="flex-fill"
                                >
                                  Todos ({resumen.total})
                                </Button>
                                <Button 
                                  variant={filtroEstadoModulo === FILTROS_ESTADO.APROBADOS ? 'success' : 'outline-success'}
                                  onClick={() => setFiltroEstadoModulo(modulo.id, FILTROS_ESTADO.APROBADOS)}
                                  className="flex-fill"
                                >
                                  <FaCheckCircle className="me-1" />Aprobados ({resumen.aprobaron})
                                </Button>
                                <Button 
                                  variant={filtroEstadoModulo === FILTROS_ESTADO.NO_APROBADOS ? 'danger' : 'outline-danger'}
                                  onClick={() => setFiltroEstadoModulo(modulo.id, FILTROS_ESTADO.NO_APROBADOS)}
                                  className="flex-fill"
                                >
                                  <FaTimesCircle className="me-1" />No aprobados ({resumen.noAprobaron})
                                </Button>
                                <Button 
                                  variant={filtroEstadoModulo === FILTROS_ESTADO.PENDIENTES ? 'warning' : 'outline-warning'}
                                  onClick={() => setFiltroEstadoModulo(modulo.id, FILTROS_ESTADO.PENDIENTES)}
                                  className="flex-fill"
                                >
                                  <FaClock className="me-1" />Pendientes ({resumen.pendientes})
                                </Button>
                              </ButtonGroup>
                            </div>

                            <div className="tabla-container">
                              <Table hover size="sm" className="tabla-detalle">
                                <thead>
                                  <tr>
                                    <th>Legajo</th>
                                    <th>Nombre</th>
                                    <th>Rol</th>
                                    <th>Progreso</th>
                                    <th>Estado</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {detalleFiltrado.length === 0 ? (
                                    <tr>
                                      <td colSpan="5" className="text-center text-muted py-4">
                                        {filtroEstadoModulo !== FILTROS_ESTADO.TODOS 
                                          ? `No hay empleados con estado "${filtroEstadoModulo}"` 
                                          : 'Sin resultados'}
                                      </td>
                                    </tr>
                                  ) : (
                                    detalleFiltrado.map((d, i) => (
                                      <tr key={i}>
                                        <td className="fw-medium">{d.legajo}</td>
                                        <td>{d.nombre}</td>
                                        <td><Badge bg="secondary" className="badge-rol">{d.rol}</Badge></td>
                                        <td>
                                          {d.porcentaje !== null ? (
                                            <div className="progress-cell">
                                              <ProgressBar 
                                                now={d.porcentaje} 
                                                variant={d.porcentaje >= 70 ? 'success' : d.porcentaje >= 40 ? 'warning' : 'danger'}
                                                className="progress-mini"
                                              />
                                              <span className="progress-text">{d.porcentaje}%</span>
                                            </div>
                                          ) : (
                                            <span className="text-muted">—</span>
                                          )}
                                        </td>
                                        <td>{getEstadoBadge(d.estado)}</td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </Table>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    );
                  })
                )}
              </Card.Body>
            )}
          </Card>
        ))}
      </Container>
    </div>
  );
};

export default ResumenCapacitaciones;
